import React from 'react';
import { Tooltip } from 'antd';
import { Gap, Gutter, Icon, Text } from '@common/components';
import { Colors } from '@common/tokens';

type LegendItemType = {
  key: string;
  badge?: string;
  /** Render the Works-suggested sparkles glyph instead of a letter badge. */
  sparkles?: boolean;
  label: string;
  tooltip: string;
  swatchModifier?: string;
};

const LEGEND_ITEMS: LegendItemType[] = [
  {
    key: 'pattern',
    badge: 'P',
    label: 'Shift pattern',
    tooltip: 'Scheduled from a recurring pattern — not manually assigned.',
    swatchModifier: 'schedule-calendar-legend__swatch--pattern',
  },
  {
    key: 'self',
    badge: 'S',
    label: 'Self-scheduled',
    tooltip: 'Self-scheduled by the staff member.',
    swatchModifier: 'schedule-calendar-legend__swatch--self',
  },
  {
    key: 'float',
    badge: 'F',
    label: 'Float coverage',
    tooltip: 'Covering from another unit in the service line.',
  },
  {
    key: 'suggested',
    sparkles: true,
    label: 'Works assigned',
    tooltip: 'Indicates this shift was assigned by the balancer.',
  },
  {
    key: 'open',
    label: 'Open shift',
    tooltip: 'No one assigned yet — coverage gap.',
    swatchModifier: 'schedule-calendar-legend__swatch--open',
  },
  {
    key: 'fixed',
    label: 'Fixed commitment',
    tooltip:
      'This worker has an approved block — such as time off, education, or leave — that prevents this shift from being reassigned.',
    swatchModifier: 'schedule-calendar-legend__swatch--fixed',
  },
];

/** Private legend documenting the {@link ScheduleCalendar} shift-cell encoding. */
export function ShiftCalendarLegend() {
  return (
    <Gutter asPadding direction="vertical" size="sm">
      <Gap wrap size="md" align="center">
        {LEGEND_ITEMS.map((item) => (
          <Tooltip key={item.key} title={item.tooltip} mouseEnterDelay={0.2}>
            <span className="schedule-calendar-legend__item">
              <span
                className={`schedule-calendar-legend__swatch ${item.swatchModifier ?? ''}`}
              >
                {item.badge ? (
                  <span className="schedule-calendar-legend__badge">
                    {item.badge}
                  </span>
                ) : null}
                {item.sparkles ? (
                  <Icon
                    icon="sparkles"
                    kind="light"
                    color="midnightRegular"
                    size="sm"
                  />
                ) : null}
              </span>
              <Text size="md" secondary>
                {item.label}
              </Text>
            </span>
          </Tooltip>
        ))}
      </Gap>

      <style jsx>{`
        .schedule-calendar-legend__item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }
        .schedule-calendar-legend__swatch {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          border-radius: 2px;
          flex-shrink: 0;
          background: ${Colors.white};
          border: 1px solid ${Colors.steel};
        }
        .schedule-calendar-legend__swatch--pattern {
          border-left: 3px solid ${Colors.steelDarkest};
        }
        .schedule-calendar-legend__swatch--self {
          border-left: 3px solid ${Colors.elmLighter};
        }
        .schedule-calendar-legend__swatch--open {
          background: ${Colors.white};
          border: 1px dashed ${Colors.steelDarker};
        }
        .schedule-calendar-legend__swatch--fixed {
          background: ${Colors.steelLightest};
          border: 1px solid ${Colors.steelLighter};
        }
        .schedule-calendar-legend__badge {
          font-size: 10px;
          font-weight: 600;
          color: ${Colors.midnightRegular};
          line-height: 1;
        }
      `}</style>
    </Gutter>
  );
}
